'use client';

import {
  blk_arc_interfaces,
  spot_msgs,
  spot_msgs_roboverse,
  spot_ncb_rms_actions_interfaces,
  test_msgs,
} from 'rclnodejs';
import { useMemo } from 'react';
import { ValueOf } from 'type-fest';

import { useDeviceName } from 'lib/api/myRobot';
import { RosSignalR } from 'lib/roslibjs';

import type * as aerial_interfaces_action from 'lib/ros-abstraction/aerial/aerial-interfaces-action';
import type * as robot_interfaces_action from 'lib/ros-abstraction/common/robot-interfaces-action';

import { IRosTypeActionMsgsGoalStatusConst } from 'types/generated/ros-msgs';
import { DeviceName } from 'types/models';

import { useFleetRosBridge } from 'hooks/signalR/useFleetRosBridge';
import { useRosBridge } from './use-ros-bridge';

export const ROS_ACTIONS = {
  OPEN_DOOR_FROM_IMAGE: (deviceName: DeviceName) => `/${deviceName}/open_door_from_image` as const,
  PICK_UP_OBJECT: (deviceName: DeviceName) => `/${deviceName}/pick_up_object` as const,
  REMOTE_MISSION_SERVICE: (deviceName: DeviceName) => `/${deviceName}/rms/call_service` as const,
  ROBOT_COMMAND: (deviceName: DeviceName) => `/${deviceName}/robot_command` as const,
  BLK_ARC_STOP_CAPTURE: (deviceName: DeviceName) =>
    `/${deviceName}/blk_arc/stop_capture_and_download` as const,
  BLK_ARC_STATIC_SCAN: (deviceName: DeviceName) =>
    `/${deviceName}/blk_arc/timed_static_scan` as const,
  FIBONACCI: (deviceName: DeviceName) => `/${deviceName}/fibonacci` as const,
  RETURN_TO_HOME: (deviceName: DeviceName) => `/${deviceName}/return_to_home` as const,
  NAVIGATE_TO_GPS: (deviceName: DeviceName) => `/${deviceName}/navigate_to_gps` as const,
  AUTO_TAKEOFF: (deviceName: DeviceName) => `/${deviceName}/auto_takeoff` as const,
} as const;

export type RosActionName = ReturnType<ValueOf<typeof ROS_ACTIONS>>;
export type DeviceActionPath = `/${string}/${string}`;

// Note: find a bedder naming as "caller"
class RosActionsCaller {
  private readonly ros: RosSignalR;
  private readonly deviceName: DeviceName;

  constructor(ros: RosSignalR, deviceName: DeviceName) {
    this.ros = ros;
    this.deviceName = deviceName;
  }

  parseResultStatus(status: number) {
    if (
      Object.values(IRosTypeActionMsgsGoalStatusConst).includes(
        status as IRosTypeActionMsgsGoalStatusConst,
      )
    ) {
      return status as IRosTypeActionMsgsGoalStatusConst;
    } else {
      return IRosTypeActionMsgsGoalStatusConst.STATUS_UNKNOWN;
    }
  }

  PickUpObject(
    goal: spot_msgs_roboverse.action.PickUpObject_Goal,
    feedbackCallback: (f: spot_msgs_roboverse.action.PickUpObject_Feedback) => void,
  ) {
    const name = ROS_ACTIONS.PICK_UP_OBJECT(this.deviceName);
    return this.ros.callAction<
      spot_msgs_roboverse.action.PickUpObject_Goal,
      spot_msgs_roboverse.action.PickUpObject_Feedback,
      spot_msgs_roboverse.action.PickUpObject_Result
    >(name, 'spot_msgs_roboverse/action/PickUpObject', goal, feedbackCallback);
  }

  async OpenDoorFromImage(
    goal: spot_msgs_roboverse.action.OpenDoorFromImage_Goal,
    feedbackCallback: (f: spot_msgs_roboverse.action.OpenDoorFromImage_Feedback) => void,
    cancelActionCreator?: (callback: unknown) => void,
  ) {
    const name = ROS_ACTIONS.OPEN_DOOR_FROM_IMAGE(this.deviceName);

    return await this.ros.callAction<
      spot_msgs_roboverse.action.OpenDoorFromImage_Goal,
      spot_msgs_roboverse.action.OpenDoorFromImage_Feedback,
      spot_msgs_roboverse.action.OpenDoorFromImage_Result
    >(
      name,
      'spot_msgs_roboverse/action/OpenDoorFromImage',
      goal,
      feedbackCallback,
      cancelActionCreator,
    );
  }

  // WIP - this is a test for using callActionReturnCancel but cancel is not working
  OpenDoorFromImageCancel(
    goal: spot_msgs_roboverse.action.OpenDoorFromImage_Goal,
    feedbackCallback: (f: spot_msgs_roboverse.action.OpenDoorFromImage_Feedback) => void,
    resultCallback: (f: spot_msgs_roboverse.action.OpenDoorFromImage_Result) => void,
  ) {
    const name = ROS_ACTIONS.OPEN_DOOR_FROM_IMAGE(this.deviceName);
    return this.ros.callActionReturnCancel<
      spot_msgs_roboverse.action.OpenDoorFromImage_Goal,
      spot_msgs_roboverse.action.OpenDoorFromImage_Feedback,
      spot_msgs_roboverse.action.OpenDoorFromImage_Result
    >(name, 'spot_msgs_roboverse/action/OpenDoorFromImage', goal, feedbackCallback, resultCallback);
  }

  RemoteMissionService(
    goal: spot_ncb_rms_actions_interfaces.action.RemoteMissionService_Goal,
    feedbackCallback: (
      f: spot_ncb_rms_actions_interfaces.action.RemoteMissionService_Feedback,
    ) => void,
  ) {
    const name = ROS_ACTIONS.REMOTE_MISSION_SERVICE(this.deviceName);
    return this.ros.callAction<
      spot_ncb_rms_actions_interfaces.action.RemoteMissionService_Goal,
      spot_ncb_rms_actions_interfaces.action.RemoteMissionService_Feedback,
      spot_ncb_rms_actions_interfaces.action.RemoteMissionService_Result
    >(name, 'spot_ncb_rms_actions_interfaces/action/RemoteMissionService', goal, feedbackCallback);
  }

  robotCommand(
    goal: spot_msgs.action.RobotCommand_Goal,
    feedbackCallback: (f: spot_msgs.action.RobotCommand_Feedback) => void = () => {},
  ) {
    const name = ROS_ACTIONS.ROBOT_COMMAND(this.deviceName);

    return this.ros.callAction<
      spot_msgs.action.RobotCommand_Goal,
      spot_msgs.action.RobotCommand_Feedback,
      spot_msgs.action.RobotCommand_Result
    >(name, 'spot_msgs/action/RobotCommand', goal, feedbackCallback);
  }

  blkArcCaptureStop(
    feedbackCallback: (f: blk_arc_interfaces.action.StopCaptureAndDownload_Feedback) => void,
  ) {
    const name = ROS_ACTIONS.BLK_ARC_STOP_CAPTURE(this.deviceName);
    return this.ros.callAction<
      blk_arc_interfaces.action.StopCaptureAndDownload_Goal,
      blk_arc_interfaces.action.StopCaptureAndDownload_Feedback,
      blk_arc_interfaces.action.StopCaptureAndDownload_Result
    >(name, 'blk_arc_interfaces/action/StopCaptureAndDownload', {}, feedbackCallback);
  }

  blkArcStaticScan(
    goal: blk_arc_interfaces.action.TimedStaticScan_Goal,
    feedbackCallback: (f: blk_arc_interfaces.action.TimedStaticScan_Feedback) => void,
  ) {
    const name = ROS_ACTIONS.BLK_ARC_STATIC_SCAN(this.deviceName);
    return this.ros.callAction<
      blk_arc_interfaces.action.TimedStaticScan_Goal,
      blk_arc_interfaces.action.TimedStaticScan_Feedback,
      blk_arc_interfaces.action.TimedStaticScan_Result
    >(name, 'blk_arc_interfaces/action/TimedStaticScan', goal, feedbackCallback);
  }

  fibonacci(
    goal: test_msgs.action.Fibonacci_Goal,
    feedbackCallback: (f: test_msgs.action.Fibonacci_Feedback) => void,
  ) {
    const name = ROS_ACTIONS.FIBONACCI(this.deviceName);
    return this.ros.callAction<
      test_msgs.action.Fibonacci_Goal,
      test_msgs.action.Fibonacci_Feedback,
      test_msgs.action.Fibonacci_Result
    >(name, 'ros2_dummy_interfaces/action/Fibonacci', goal, feedbackCallback);
  }

  returnToHome(
    goal: robot_interfaces_action.ReturnToHome_Goal,
    feedbackCallback: (f: robot_interfaces_action.ReturnToHome_Feedback) => void,
  ) {
    const name = ROS_ACTIONS.RETURN_TO_HOME(this.deviceName);
    return this.ros.callAction<
      robot_interfaces_action.ReturnToHome_Goal,
      robot_interfaces_action.ReturnToHome_Feedback,
      robot_interfaces_action.ReturnToHome_Result
    >(name, 'robot_interfaces/action/ReturnToHome', goal, feedbackCallback);
  }

  navigateToGps(
    goal: robot_interfaces_action.NavigateToGPS_Goal,
    feedbackCallback: (f: robot_interfaces_action.NavigateToGPS_Feedback) => void,
  ) {
    const name = ROS_ACTIONS.NAVIGATE_TO_GPS(this.deviceName);
    return this.ros.callAction<
      robot_interfaces_action.NavigateToGPS_Goal,
      robot_interfaces_action.NavigateToGPS_Feedback,
      robot_interfaces_action.NavigateToGPS_Result
    >(name, 'robot_interfaces/action/NavigateToGPS', goal, feedbackCallback);
  }

  autoTakeoff(
    goal: aerial_interfaces_action.AutoTakeoff_Goal,
    feedbackCallback: (f: aerial_interfaces_action.AutoTakeoff_Feedback) => void,
  ) {
    const name = ROS_ACTIONS.AUTO_TAKEOFF(this.deviceName);
    return this.ros.callAction<
      aerial_interfaces_action.AutoTakeoff_Goal,
      aerial_interfaces_action.AutoTakeoff_Feedback,
      aerial_interfaces_action.AutoTakeoff_Result
    >(name, 'aerial_interfaces/action/AutoTakeoff', goal, feedbackCallback);
  }
}

/**
 * Todo: check if we merge this with "useRos"
 */
export function useRosAction() {
  const { ros } = useRosBridge();
  const { deviceName } = useDeviceName();

  const RosActions = useMemo(() => new RosActionsCaller(ros, deviceName), [deviceName]);

  return { RosActions };
}

/**
 * Variant for use outside a robot-specific route (e.g. fleet map / AI Copilot).
 * Accepts an explicit deviceName instead of reading from context.
 * Requires FleetRosBridgeProvider in the component tree.
 */
export function useRosActionForDevice(deviceName: string) {
  const ros = useFleetRosBridge()(deviceName);

  const RosActions = useMemo(
    () => new RosActionsCaller(ros, deviceName as DeviceName),
    [ros, deviceName],
  );

  return { RosActions };
}
